import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import styles from "./Add.module.scss";
import { api } from "../../../../services";
import { CONSTANTS } from "../../../../library/common/constant";

import { throttle } from "lodash";

const {
  CREATE_ABOUT_US_TITLE,
  HEADING_LABEL,
  HEADING_LABEL_HINDI,
  DESCRIPTION_LABEL,
  DESCRIPTION_LABEL_HINDI,
  ORDER_LABEL,
  IS_IMAGE_LABEL,
  LIST_ITEM_TRUE,
  LIST_ITEM_FALSE,
  IMAGE_PREVIEW_LABEL,
  YOUTUBE_LINK_LABEL,
  CREATE_BUTTON,
  UPDATE_BUTTON,
  THROTTLE_DURATION,
} = CONSTANTS;

const Add = (props) => {
  const navigate = useNavigate();
  const { id: singleId } = useParams();
  const [formData, setFormData] = useState({
    cardImage: "",
    heading: { en: "", hi: "" },
    isImage: true,
    description: { en: "", hi: "" },
    order: 0,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};

    if (formData.isImage && !formData.cardImage) {
      newErrors.cardImage = "Image file is required";
    } else if (
      !formData.isImage &&
      formData.cardImage &&
      !/^https?:\/\/.+/.test(formData.cardImage)
    ) {
      newErrors.cardImage = "Please enter a valid Video URL";
    }
    newErrors.heading = {};
    if (!formData.heading?.en?.trim()) {
      newErrors.heading.en = "Heading (English) is required";
    }
    if (!formData.heading?.hi?.trim()) {
      newErrors.heading.hi = "Heading (Hindi) is required";
    }
    if (Object.keys(newErrors.heading).length === 0) {
      delete newErrors.heading;
    }
    newErrors.description = {};
    if (!formData.description?.en?.trim()) {
      newErrors.description.en = "Description (English) is required";
    }
    if (!formData.description?.hi?.trim()) {
      newErrors.description.hi = "Description (Hindi) is required";
    }
    if (Object.keys(newErrors.description).length === 0) {
      delete newErrors.description;
    }
    if (formData.order < 0) {
      newErrors.order = "Please enter a valid order";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = async (e) => {
    const { name, value, type, files } = e.target;

    if (name === "cardImage" && files?.length) {
      const file = files[0];
      const base64 = await convertToBase64(file);

      setFormData((prev) => ({
        ...prev,
        cardImage: file,
        previewImage: base64,
      }));

      setErrors((prev) => ({ ...prev, cardImage: "" }));
      return;
    }

    if (name === "isImage") {
      setFormData((prev) => ({
        ...prev,
        isImage: Number(value) === 1,
      }));
      setErrors((prev) => ({ ...prev, isImage: "" }));
      return;
    }

    if (
      ["heading.en", "heading.hi", "description.en", "description.hi"].includes(
        name
      )
    ) {
      const [key, lang] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          [lang]: value,
        },
      }));
      setErrors((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          [lang]: "",
        },
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    const form = new FormData();
    form.append("isImage", formData.isImage);
    form.append("order", Number(formData.order));
    form.append("heading", JSON.stringify(formData.heading));
    form.append("description", JSON.stringify(formData.description));

    if (formData.isImage && formData.cardImage instanceof File) {
      form.append("cardImage", formData.cardImage);
    } else {
      form.append("cardImage", formData.cardImage);
    }

    formRef.current = form;

    try {
      if (singleId) {
        handleEdit();
      } else {
        handleAdd();
      }
    } catch (error) {
      console.error("Form submission error: ", error);
    }
  };

  const returnBack = () => {
    navigate("/pages/about-us", { replace: true });
  };

  const handleAdd = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.post("/aboutus", form);
      returnBack();
    } catch (error) {
      console.error("Failed to post: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEdit = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.patch(`/aboutus/${singleId}`, form);
      returnBack();
    } catch (error) {
      console.error("Failed to save: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const convertToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        return resolve(reader?.result ? reader?.result : "");
      };
      reader.onerror = (error) => {
        return resolve("");
      };
    });
  };

  useEffect(() => {
    const fetchBannerDetails = async () => {
      const data = await api.get(`/aboutus/${singleId}`);
      setFormData({
        ...data?.data,
        previewImage: data?.data?.isImage
          ? data?.data?.cardImage
            ? data?.data?.cardImage
            : ""
          : "",
      });
    };
    if (singleId) fetchBannerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.carouselAdd}>
      <Container maxWidth="md">
        <Card className={styles.styledCard}>
          <CardContent>
            <Typography variant="h5" component="h2" className={styles.title}>
              {CREATE_ABOUT_US_TITLE}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Box className={styles.formContainer}>
                <TextField
                  fullWidth
                  label={HEADING_LABEL}
                  type="text"
                  name="heading.en"
                  value={formData.heading?.en || ""}
                  onChange={handleChange}
                  error={!!errors.heading?.en}
                  helperText={errors.heading?.en}
                  className={styles.textField}
                />

                <TextField
                  fullWidth
                  type="text"
                  label={DESCRIPTION_LABEL}
                  name="description.en"
                  value={formData.description?.en || ""}
                  onChange={handleChange}
                  error={!!errors.description?.en}
                  multiline
                  rows={4}
                  helperText={errors.description?.en}
                  className={styles.textField}
                />

                <TextField
                  fullWidth
                  label={HEADING_LABEL_HINDI}
                  type="text"
                  name="heading.hi"
                  value={formData.heading?.hi || ""}
                  onChange={handleChange}
                  error={!!errors.heading?.hi}
                  helperText={errors.heading?.hi}
                  className={styles.textField}
                />

                <TextField
                  fullWidth
                  type="text"
                  label={DESCRIPTION_LABEL_HINDI}
                  name="description.hi"
                  value={formData.description?.hi || ""}
                  onChange={handleChange}
                  error={!!errors.description?.hi}
                  multiline
                  rows={4}
                  helperText={errors.description?.hi}
                  className={styles.textField}
                />

                <TextField
                  fullWidth
                  label={ORDER_LABEL}
                  name="order"
                  type="number"
                  value={formData.order || ""}
                  onChange={handleChange}
                  error={!!errors.order}
                  helperText={errors.order}
                  className={styles.textField}
                />

                <FormControl fullWidth margin="dense" error={!!errors?.isImage}>
                  <InputLabel id="isimage-label">Is Image</InputLabel>
                  <Select
                    labelId="isimage-label"
                    fullWidth
                    label={IS_IMAGE_LABEL}
                    name="isImage"
                    value={formData.isImage ? 1 : 0}
                    onChange={handleChange}
                    error={!!errors.isImage}
                    className={styles.textField}
                  >
                    <MenuItem value={1}>{LIST_ITEM_TRUE}</MenuItem>
                    <MenuItem value={0}>{LIST_ITEM_FALSE}</MenuItem>
                  </Select>

                  <FormHelperText>{errors?.isImage || ""}</FormHelperText>
                </FormControl>

                {formData.isImage ? (
                  <Box className={styles.imagePreview}>
                    <label>
                      {formData?.previewImage ? (
                        <img
                          src={formData?.previewImage}
                          alt="Preview"
                          className={styles.previewImage}
                        />
                      ) : (
                        <Box className={styles.previewIcon}>
                          <AddPhotoAlternateIcon className={styles.icon} />
                          <Typography variant="body2" color="textSecondary">
                            {IMAGE_PREVIEW_LABEL}
                          </Typography>
                        </Box>
                      )}
                      <input
                        type="file"
                        name="cardImage"
                        accept="image/*"
                        onChange={handleChange}
                        className={styles.fileInput}
                        style={{ display: "none" }}
                      />
                    </label>
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    type="text"
                    label={YOUTUBE_LINK_LABEL}
                    name="cardImage"
                    value={formData.cardImage || ""}
                    onChange={handleChange}
                    error={!!errors.cardImage}
                    helperText={errors.cardImage}
                    className={styles.textField}
                  />
                )}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  className={styles.submitButton}
                  disabled={isLoading}
                >
                  {!singleId ? CREATE_BUTTON : UPDATE_BUTTON}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default Add;
